import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../config/config';
import { 
  FaFileInvoiceDollar, FaPlus, FaEdit, FaTrash, FaFilter, FaSort,
  FaCheckCircle, FaExclamationCircle, FaClock, FaEye, FaDownload,
  FaCalendarAlt, FaMoneyBillWave
} from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { usePermission } from '../../../hooks/usePermission';

const Invoices = () => {
  const { can } = usePermission();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({ paid: 0, unpaid: 0, partially_paid: 0 });
  
  // Filters and sorting
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({
    client_name: '',
    amount: '',
    due_date: '',
    total_installments: 3,
    notes: '',
    installment_amounts: [],
    include_tva: true,
    tva_rate: 19
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${SERVER}/invoices`, { withCredentials: true });
      setInvoices(response.data.invoices);
      setTotals(response.data.totals);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInvoice) {
        if (!can('invoices.edit')) return alert("Permission denied");
        await axios.put(`${SERVER}/invoices/${editingInvoice.id}`, invoiceForm, { withCredentials: true });
      } else {
        if (!can('invoices.create')) return alert("Permission denied");
        await axios.post(`${SERVER}/invoices`, invoiceForm, { withCredentials: true });
      }
      closeModal();
      fetchInvoices();
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Failed to save invoice');
    }
  };

  const handleDelete = async (id) => {
    if (!can('invoices.delete')) return alert("Permission denied");
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await axios.delete(`${SERVER}/invoices/${id}`, { withCredentials: true });
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Failed to delete invoice');
    }
  };

  const openAddModal = () => {
    if (!can('invoices.create')) return alert("Permission denied");
    setEditingInvoice(null);
    const defaultInstallments = 3;
    setInvoiceForm({
      client_name: '',
      amount: '',
      due_date: '',
      total_installments: defaultInstallments,
      notes: '',
      installment_amounts: Array(defaultInstallments).fill(''),
      include_tva: true,
      tva_rate: 19
    });
    setShowModal(true);
  };

  const openEditModal = (invoice) => {
    if (!can('invoices.edit')) return alert("Permission denied");
    setEditingInvoice(invoice);
    const installmentCount = invoice.total_installments || 1;
    const equalAmount = (parseFloat(invoice.amount) / installmentCount).toFixed(2);
    setInvoiceForm({
      client_name: invoice.client_name,
      amount: invoice.amount,
      due_date: invoice.due_date.split('T')[0],
      total_installments: installmentCount,
      notes: invoice.notes || '',
      installment_amounts: Array(installmentCount).fill(equalAmount),
      include_tva: invoice.include_tva !== false,
      tva_rate: invoice.tva_rate || 19
    });
    setShowModal(true);
  };

  const openDetailsModal = async (invoiceId) => {
    if (!can('invoices.view')) return alert("Permission denied");
    try {
      const response = await axios.get(`${SERVER}/invoices/${invoiceId}/details`, { withCredentials: true });
      setSelectedInvoice(response.data.invoice);
      setInstallments(response.data.installments);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      alert('Failed to load invoice details');
    }
  };

  const markInstallmentPaid = async (installmentId) => {
    try {
      await axios.put(`${SERVER}/invoices/installments/${installmentId}/pay`, {
        payment_method: 'cash',
        notes: ''
      }, { withCredentials: true });
      
      if (selectedInvoice) {
        openDetailsModal(selectedInvoice.id);
      }
      fetchInvoices();
    } catch (error) {
      console.error('Error marking installment as paid:', error);
      alert('Failed to mark installment as paid');
    }
  };

  const markInstallmentUnpaid = async (installmentId) => {
    if (!window.confirm('Are you sure you want to mark this installment as unpaid?')) return;
    try {
      await axios.put(`${SERVER}/invoices/installments/${installmentId}/unpay`, {}, { withCredentials: true });
      
      if (selectedInvoice) {
        openDetailsModal(selectedInvoice.id);
      }
      fetchInvoices();
    } catch (error) {
      console.error('Error marking installment as unpaid:', error);
      alert('Failed to mark installment as unpaid');
    }
  };

  const downloadPDF = async (invoiceId) => {
    // Direct approach: Open PDF in new tab (works with IDM)
    window.open(`${SERVER}/invoices/${invoiceId}/pdf`, '_blank');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingInvoice(null);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedInvoice(null);
    setInstallments([]);
  };

  // Handle installment count change
  const handleInstallmentCountChange = (count) => {
    const newCount = parseInt(count);
    const totalAmount = parseFloat(invoiceForm.amount) || 0;
    const equalAmount = totalAmount > 0 ? (totalAmount / newCount).toFixed(2) : '';
    
    setInvoiceForm({
      ...invoiceForm,
      total_installments: newCount,
      installment_amounts: Array(newCount).fill(equalAmount)
    });
  };

  // Handle individual installment amount change
  const handleInstallmentAmountChange = (index, value) => {
    const newAmounts = [...invoiceForm.installment_amounts];
    newAmounts[index] = value;
    setInvoiceForm({
      ...invoiceForm,
      installment_amounts: newAmounts
    });
  };

  // Auto-fill equal division
  const autoFillEqualDivision = () => {
    const totalAmount = parseFloat(invoiceForm.amount) || 0;
    const count = invoiceForm.total_installments;
    const equalAmount = (totalAmount / count).toFixed(2);
    
    setInvoiceForm({
      ...invoiceForm,
      installment_amounts: Array(count).fill(equalAmount)
    });
  };

  // Calculate total from installments
  const calculateInstallmentTotal = () => {
    return invoiceForm.installment_amounts.reduce((sum, amount) => {
      return sum + (parseFloat(amount) || 0);
    }, 0).toFixed(2);
  };

  // Calculate TVA breakdown
  const calculateTVA = () => {
    const amount = parseFloat(invoiceForm.amount) || 0;
    const rate = parseFloat(invoiceForm.tva_rate) || 19;
    
    if (invoiceForm.include_tva) {
      const subtotal = amount / (1 + rate / 100);
      const tva = amount - subtotal;
      return { subtotal, tva, total: amount };
    } else {
      const tva = amount * (rate / 100);
      return { subtotal: amount, tva, total: amount + tva };
    }
  };

  // Filter and sort invoices
  const getFilteredSortedInvoices = () => {
    let filtered = invoices;
    
    // Apply filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(inv => inv.payment_status === filterStatus);
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let compareA, compareB;
      
      if (sortBy === 'date') {
        compareA = new Date(a.due_date);
        compareB = new Date(b.due_date);
      } else if (sortBy === 'amount') {
        compareA = parseFloat(a.amount);
        compareB = parseFloat(b.amount);
      } else if (sortBy === 'client') {
        compareA = a.client_name.toLowerCase();
        compareB = b.client_name.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return compareA > compareB ? 1 : -1;
      } else {
        return compareA < compareB ? 1 : -1;
      }
    });
    
    return sorted;
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { 
        gradient: 'bg-gradient-to-r from-green-500 to-emerald-600', 
        icon: <FaCheckCircle />,
        label: 'Paid'
      },
      unpaid: { 
        gradient: 'bg-gradient-to-r from-red-500 to-rose-600', 
        icon: <FaExclamationCircle />,
        label: 'Unpaid'
      },
      partially_paid: { 
        gradient: 'bg-gradient-to-r from-yellow-500 to-orange-600', 
        icon: <FaClock />,
        label: 'Partially Paid'
      }
    };
    
    const badge = badges[status] || badges.unpaid;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg ${badge.gradient}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredInvoices = getFilteredSortedInvoices();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-blue-600 absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 rounded-3xl shadow-2xl p-8 animate-scaleIn">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500/20 rounded-full -ml-48 -mb-48 blur-3xl" />
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <FaFileInvoiceDollar className="text-3xl text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-1">Invoices & Payments</h2>
              <p className="text-emerald-100 text-lg">Manage client invoices and track payments</p>
            </div>
          </div>
          {can('invoices.create') && (
            <button 
              onClick={openAddModal}
              className="px-6 py-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl border border-white/30 flex items-center gap-2 group"
            >
              <FaPlus className="group-hover:rotate-90 transition-transform duration-300" />
              <span>New Invoice</span>
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="group relative bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-xl p-6 overflow-hidden animate-slideUp hover:scale-105 transition-transform duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-semibold uppercase tracking-wider">Total Paid</p>
              <h3 className="text-4xl font-bold text-white mt-2">{formatCurrency(totals.paid)}</h3>
            </div>
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <FaCheckCircle className="text-white text-2xl" />
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl shadow-xl p-6 overflow-hidden animate-slideUp hover:scale-105 transition-transform duration-300" style={{ animationDelay: '0.1s' }}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-semibold uppercase tracking-wider">Total Unpaid</p>
              <h3 className="text-4xl font-bold text-white mt-2">{formatCurrency(totals.unpaid)}</h3>
            </div>
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <FaExclamationCircle className="text-white text-2xl" />
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl shadow-xl p-6 overflow-hidden animate-slideUp hover:scale-105 transition-transform duration-300" style={{ animationDelay: '0.2s' }}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-semibold uppercase tracking-wider">Partially Paid</p>
              <h3 className="text-4xl font-bold text-white mt-2">{formatCurrency(totals.partially_paid)}</h3>
            </div>
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <FaClock className="text-white text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Sorting */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-6 animate-slideUp" style={{ animationDelay: '0.3s' }}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <FaFilter className="text-white" />
            </div>
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-semibold text-slate-700 transition-all"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="partially_paid">Partially Paid</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
              <FaSort className="text-white" />
            </div>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none font-semibold text-slate-700 transition-all"
            >
              <option value="date">Sort by Date</option>
              <option value="amount">Sort by Amount</option>
              <option value="client">Sort by Client</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="w-10 h-10 border-2 border-slate-200 rounded-xl hover:bg-slate-100 font-bold text-slate-700 transition-all hover:scale-110 flex items-center justify-center"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          <div className="ml-auto px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200/50">
            <span className="text-sm font-semibold text-slate-700">
              Showing {filteredInvoices.length} of {invoices.length} invoices
            </span>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200/50 overflow-hidden animate-slideUp" style={{ animationDelay: '0.4s' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">👤 Client</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">💰 Amount</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">📊 Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">📅 Due Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">⚙️ Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredInvoices.length > 0 ? (
                filteredInvoices.map((invoice, index) => (
                  <tr key={invoice.id} className="hover:bg-blue-50/50 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200">
                          <FaFileInvoiceDollar className="text-white text-lg" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-900">{invoice.client_name}</div>
                          <div className="text-xs text-slate-500 font-semibold">Invoice #{invoice.id}</div>
                          {invoice.total_installments > 1 && (
                            <div className="text-xs text-blue-600 font-semibold mt-1">
                              {invoice.paid_installments || 0}/{invoice.total_installments} installments paid
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-lg font-bold text-slate-900">{formatCurrency(invoice.amount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invoice.payment_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">{formatDate(invoice.due_date)}</div>
                      {new Date(invoice.due_date) < new Date() && invoice.payment_status !== 'paid' && (
                        <div className="text-xs text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded-full inline-block mt-1">
                          ⚠️ Overdue
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {can('invoices.view') && (
                          <button
                            onClick={() => openDetailsModal(invoice.id)}
                            className="p-2.5 text-green-600 hover:bg-green-100 rounded-xl transition-all duration-200 hover:scale-110"
                            title="View Details"
                          >
                            <FaEye size={16} />
                          </button>
                        )}
                        {can('invoices.view') && (
                          <button
                            onClick={() => downloadPDF(invoice.id)}
                            className="p-2.5 text-purple-600 hover:bg-purple-100 rounded-xl transition-all duration-200 hover:scale-110"
                            title="Download PDF"
                          >
                            <FaDownload size={16} />
                          </button>
                        )}
                        {can('invoices.edit') && (
                          <button
                            onClick={() => openEditModal(invoice)}
                            className="p-2.5 text-blue-600 hover:bg-blue-100 rounded-xl transition-all duration-200 hover:scale-110"
                            title="Edit Invoice"
                          >
                            <FaEdit size={16} />
                          </button>
                        )}
                        {can('invoices.delete') && (
                          <button
                            onClick={() => handleDelete(invoice.id)}
                            className="p-2.5 text-red-600 hover:bg-red-100 rounded-xl transition-all duration-200 hover:scale-110"
                            title="Delete Invoice"
                          >
                            <FaTrash size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center">
                        <FaFileInvoiceDollar className="text-slate-400 text-3xl" />
                      </div>
                      <p className="text-slate-500 font-semibold text-lg">No invoices found</p>
                      <p className="text-slate-400 text-sm">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Details Modal */}
      {showDetailsModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fadeIn overflow-y-auto" style={{ position: 'fixed', top: -40, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-3xl w-full max-w-4xl animate-scaleIn overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex justify-between items-center sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <FaFileInvoiceDollar className="text-2xl text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Invoice #{selectedInvoice.id}</h3>
                  <p className="text-blue-100 text-sm">{selectedInvoice.client_name}</p>
                </div>
              </div>
              <button 
                onClick={closeDetailsModal}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 group"
              >
                <MdClose className="text-xl text-white group-hover:rotate-90 transition-transform duration-300" />
              </button>
            </div>
            
            <div className="p-8 space-y-6">
              {/* Invoice Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border-2 border-blue-200">
                  <p className="text-xs text-slate-600 font-semibold uppercase">Total Amount (TTC)</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(selectedInvoice.amount)}</p>
                  {selectedInvoice.include_tva && (
                    <p className="text-xs text-slate-500 mt-1">
                      HT: {formatCurrency(selectedInvoice.subtotal)} + TVA: {formatCurrency(selectedInvoice.tva_amount)}
                    </p>
                  )}
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border-2 border-green-200">
                  <p className="text-xs text-slate-600 font-semibold uppercase">Installments Paid</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{selectedInvoice.paid_installments}/{selectedInvoice.total_installments}</p>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-4 rounded-xl border-2 border-yellow-200">
                  <p className="text-xs text-slate-600 font-semibold uppercase">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedInvoice.payment_status)}</div>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div className="bg-slate-50 p-4 rounded-xl border-2 border-slate-200">
                  <p className="text-xs text-slate-600 font-semibold uppercase mb-2">Notes</p>
                  <p className="text-sm text-slate-700">{selectedInvoice.notes}</p>
                </div>
              )}

              {/* Installments List */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <FaMoneyBillWave className="text-blue-600" />
                    Payment Schedule
                  </h4>
                  <button
                    onClick={() => downloadPDF(selectedInvoice.id)}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
                  >
                    <FaDownload />
                    Download PDF
                  </button>
                </div>

                <div className="space-y-3">
                  {installments.map((installment) => (
                    <div 
                      key={installment.id}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        installment.status === 'paid' 
                          ? 'bg-green-50 border-green-200' 
                          : installment.status === 'overdue'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white ${
                            installment.status === 'paid' 
                              ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                              : installment.status === 'overdue'
                              ? 'bg-gradient-to-br from-red-500 to-rose-600'
                              : 'bg-gradient-to-br from-slate-500 to-slate-600'
                          }`}>
                            #{installment.installment_number}
                          </div>
                          <div>
                            <p className="font-bold text-slate-900">{formatCurrency(installment.amount)}</p>
                            <p className="text-sm text-slate-600">
                              Due: {formatDate(installment.due_date)}
                              {installment.paid_date && ` • Paid: ${formatDate(installment.paid_date)}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(installment.status)}
                          {can('invoices.edit') && (
                            <>
                              {installment.status !== 'paid' ? (
                                <button
                                  onClick={() => markInstallmentPaid(installment.id)}
                                  className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl text-sm"
                                >
                                  Mark as Paid
                                </button>
                              ) : (
                                <button
                                  onClick={() => markInstallmentUnpaid(installment.id)}
                                  className="px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl text-sm"
                                >
                                  Mark as Unpaid
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fadeIn overflow-y-auto" style={{ position: 'fixed', top: -40, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-3xl w-full max-w-md animate-scaleIn shadow-2xl my-8 max-h-[95vh] flex flex-col">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 flex justify-between items-center sticky top-0 z-10 rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <FaFileInvoiceDollar className="text-2xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">
                  {editingInvoice ? 'Edit Invoice' : 'New Invoice'}
                </h3>
              </div>
              <button 
                onClick={closeModal}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 group"
              >
                <MdClose className="text-xl text-white group-hover:rotate-90 transition-transform duration-300" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto flex-1">
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                  <span>👤</span>
                  Client Name
                </label>
                <input
                  type="text"
                  required
                  value={invoiceForm.client_name}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, client_name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                  placeholder="Enter client name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                    <span>💰</span>
                    Total Amount (TND)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={invoiceForm.amount}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                    <span>🔢</span>
                    Installments
                  </label>
                  <select
                    value={invoiceForm.total_installments}
                    onChange={(e) => handleInstallmentCountChange(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                  >
                    <option value={1}>1 (Full Payment)</option>
                    <option value={2}>2 installments</option>
                    <option value={3}>3 installments</option>
                    <option value={4}>4 installments</option>
                    <option value={6}>6 installments</option>
                    <option value={12}>12 installments</option>
                  </select>
                </div>
              </div>

              {/* Installment Amounts */}
              {invoiceForm.total_installments > 1 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border-2 border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-slate-700 flex items-center gap-2">
                      <FaMoneyBillWave className="text-blue-600" />
                      Installment Amounts
                    </h4>
                    <button
                      type="button"
                      onClick={autoFillEqualDivision}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg font-semibold transition-all"
                    >
                      Auto-fill Equal Division
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Array.from({ length: invoiceForm.total_installments }).map((_, index) => (
                      <div key={index}>
                        <label className="text-xs font-semibold text-slate-600 mb-1 block">
                          Installment #{index + 1}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          value={invoiceForm.installment_amounts[index] || ''}
                          onChange={(e) => handleInstallmentAmountChange(index, e.target.value)}
                          className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-3 pt-3 border-t-2 border-blue-200 flex justify-between items-center text-sm">
                    <span className="font-semibold text-slate-700">Total from installments:</span>
                    <span className={`font-bold text-lg ${
                      parseFloat(calculateInstallmentTotal()) === parseFloat(invoiceForm.amount) 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {calculateInstallmentTotal()} TND
                    </span>
                  </div>
                  {parseFloat(calculateInstallmentTotal()) !== parseFloat(invoiceForm.amount) && invoiceForm.amount && (
                    <div className="mt-2 text-xs text-red-600 font-semibold bg-red-50 px-3 py-2 rounded-lg">
                      ⚠️ Warning: Installment total ({calculateInstallmentTotal()} TND) doesn't match invoice amount ({parseFloat(invoiceForm.amount).toFixed(2)} TND)
                    </div>
                  )}
                </div>
              )}

              {/* TVA Section */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-slate-700 flex items-center gap-2">
                    <span>💳</span>
                    TVA (Tax)
                  </h4>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invoiceForm.include_tva}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, include_tva: e.target.checked })}
                      className="w-5 h-5 rounded text-purple-600 focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="text-sm font-semibold text-slate-700">Include TVA</span>
                  </label>
                </div>
                
                {invoiceForm.include_tva && invoiceForm.amount && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Subtotal (HT):</span>
                      <span className="font-bold text-slate-900">{calculateTVA().subtotal.toFixed(2)} TND</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">TVA ({invoiceForm.tva_rate}%):</span>
                      <span className="font-bold text-slate-900">{calculateTVA().tva.toFixed(2)} TND</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-purple-300">
                      <span className="font-bold text-slate-700">Total (TTC):</span>
                      <span className="font-bold text-lg text-purple-600">{calculateTVA().total.toFixed(2)} TND</span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                  <span>📅</span>
                  First Payment Due Date
                </label>
                <input
                  type="date"
                  required
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                  <span>📝</span>
                  Notes (Optional)
                </label>
                <textarea
                  value={invoiceForm.notes}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                  placeholder="Add any additional notes..."
                  rows="3"
                />
              </div>

            </form>
            
            <div className="p-6 bg-slate-50 border-t-2 border-slate-200 sticky bottom-0 rounded-b-3xl">
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {editingInvoice ? 'Update' : 'Create'} Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
